/**
 * The job check-up (#1669) — "why can't this job go out, and what fixes it".
 *
 * The server does the diagnosis (src/server/services/jobCheckup.ts); this is
 * the read, the one-tap fix, and the bus the job page uses to open the Help
 * Desk panel. Two screens read the same query key (the job page strip and the
 * Help Desk panel), so they can never tell the office different things.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";

export type CheckupFix =
    | { kind: "refile_document"; label: string; attachmentId: string }
    | { kind: "mark_filing_submitted"; label: string; submissionId: string }
    | { kind: "waive_filing"; label: string; submissionId: string }
    | { kind: "release_packet"; label: string }
    /** Another device's city report names this one: link it (2026-10-28). */
    | { kind: "link_shared_report"; label: string; attachmentId: string; assetId: string }
    /** Link a small DC/RP as the DCDA/RPDA's bypass. */
    | { kind: "link_bypass"; label: string; assetId: string; parentAssetId: string };

export interface CheckupProblem {
    id: string;
    /** blocked = the office can fix it here · attention = someone else's move. */
    severity: "blocked" | "attention";
    title: string;
    detail: string;
    fix?: CheckupFix;
    /** Where it gets fixed when it isn't a one-tap fix (#1701). */
    link?: { label: string; href: string };
}

export interface CheckupCheck {
    id: string;
    label: string;
    ok: boolean;
}

export interface JobCheckup {
    jobId: string;
    clear: boolean;
    held: boolean;
    problems: CheckupProblem[];
    checks: CheckupCheck[];
    /** #1674 — first name of whoever hand-offs go to (org owner); null if unset. */
    handoffTo?: string | null;
}

export interface FixResult {
    ok: boolean;
    message: string;
    checkup: JobCheckup | null;
}

export const jobCheckupKey = (jobId: string | undefined) => ["job-checkup", jobId] as const;

export function useJobCheckup(jobId: string | undefined, enabled = true) {
    return useQuery<JobCheckup>({
        queryKey: jobCheckupKey(jobId),
        queryFn: async () => {
            const r = await api.get<{ checkup: JobCheckup; handoffTo?: string | null }>(`/api/jobs/${jobId}/checkup`);
            return { ...r.checkup, handoffTo: r.handoffTo ?? null };
        },
        enabled: !!jobId && enabled,
        staleTime: 15_000,
        retry: false,
    });
}

function fixTarget(fix: CheckupFix): string | null {
    if (fix.kind === "refile_document") return fix.attachmentId;
    if (fix.kind === "mark_filing_submitted" || fix.kind === "waive_filing") return fix.submissionId;
    if (fix.kind === "link_shared_report" || fix.kind === "link_bypass") return fix.assetId;
    return null;
}

/**
 * Run one fix. Resolves with the server's plain-English outcome either way —
 * "it didn't work, here's why" is an answer for the office, not an error toast.
 */
export function useCheckupFix(jobId: string) {
    const qc = useQueryClient();
    return useMutation<FixResult, Error, CheckupFix>({
        mutationFn: async (fix) => {
            try {
                return await api.post<FixResult>(`/api/jobs/${jobId}/checkup/fix`, {
                    kind: fix.kind,
                    targetId: fixTarget(fix),
                });
            } catch (e) {
                // 409 = the job moved under the screen; the body still carries
                // a message and the fresh check-up.
                if (e instanceof ApiError && e.status === 409 && e.body && typeof e.body === "object") {
                    return e.body as FixResult;
                }
                throw e;
            }
        },
        onSuccess: (res) => {
            if (res.checkup)
                qc.setQueryData<JobCheckup>(jobCheckupKey(jobId), (prev) => ({
                    ...res.checkup!,
                    handoffTo: prev?.handoffTo ?? null,
                }));
            else void qc.invalidateQueries({ queryKey: jobCheckupKey(jobId) });
            // A fix can file a report, close a filing or send the packet —
            // everything on the job page that shows those needs a re-read.
            void qc.invalidateQueries({ queryKey: ["job", jobId] });
            void qc.invalidateQueries({ queryKey: ["packet-readiness", jobId] });
        },
    });
}

/* ── Hand-off (#1674) ───────────────────────────────────────────────────── */

export interface HandoffRequest {
    message: string;
    urgent: boolean;
    problemTitle?: string | null;
    fixResult?: string | null;
}

export interface HandoffResult {
    findingId: string;
    handoffTo: string | null;
    /** False = hand-off isn't switched on yet; the case waits on the board. */
    goesToClaude: boolean;
}

/** Send what the check-up couldn't fix to the owner, with the job attached. */
export function useCheckupHandoff(jobId: string) {
    return useMutation<HandoffResult, Error, HandoffRequest>({
        mutationFn: (body) => api.post<HandoffResult>(`/api/jobs/${jobId}/checkup/handoff`, body),
    });
}

/* ── Help Desk open bus ─────────────────────────────────────────────────── */

export const HELP_DESK_OPEN_EVENT = "help-desk:open";
/** Close the panel (e.g. "Show me" on a phone, where the panel covers the page). */
export const HELP_DESK_CLOSE_EVENT = "help-desk:close";

/** Open the top-bar Help Desk panel from anywhere (e.g. the job page strip). */
export function openHelpDesk(): void {
    window.dispatchEvent(new CustomEvent(HELP_DESK_OPEN_EVENT));
}
